import {
    Button,
    Card,
    Checkbox,
    Loader,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { BASE_API_URL } from '@api/index';

const MARKETPLACE_NAMES: Record<string, string> = {
    A2ZCKPGQLLOZFI: 'Amazon India (IN)',
    ATVPDKIKX0DER: 'Amazon United States (US)',
    A1F83G8C2ARO7P: 'Amazon United Kingdom (UK)',
    A1PA6795UKMFR9: 'Amazon Germany (DE)',
    A13V1IB3VIYZZH: 'Amazon France (FR)',
    A1RKKUPIHCS9HS: 'Amazon Spain (ES)',
    APJ6JRA9NG5V4: 'Amazon Italy (IT)',
    A1805IZSGTT6HS: 'Amazon Netherlands (NL)',
    A2NODRKZP88ZB9: 'Amazon Sweden (SE)',
    A2VIGQ35RCS4UG: 'Amazon UAE (AE)',
    A17E79C6D8DWNP: 'Amazon Saudi Arabia (SA)',
};

const AmazonMarketplaceSelect: FC = () => {
    const nav = useNavigate();
    const [params] = useSearchParams();

    const connectionToken = params.get('connection_token') ?? '';
    const marketplacesParam = params.get('marketplaces') ?? '';
    const availableIds = marketplacesParam
        ? marketplacesParam.split(',').filter(Boolean)
        : [];

    const [selected, setSelected] = useState<string[]>(availableIds);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        if (!connectionToken) {
            nav('/login');
        }
    }, [connectionToken, nav]);

    const toggle = useCallback((id: string) => {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    }, []);

    const confirm = useCallback(async () => {
        if (selected.length === 0) {
            setErr('Please select at least one marketplace.');
            return;
        }
        setErr(null);
        setSaving(true);
        try {
            const res = await fetch(
                `${BASE_API_URL}/api/v1/amazon/confirm-marketplaces`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        connectionToken,
                        selectedMarketplaceIds: selected,
                    }),
                },
            );
            if (!res.ok) throw new Error('Failed to save selection');
            nav('/login?amazon_setup_complete=true');
        } catch {
            setErr(
                'Something went wrong. Please try again or contact support.',
            );
            setSaving(false);
        }
    }, [connectionToken, selected, nav]);

    if (!connectionToken) {
        return (
            <Stack align="center" justify="center" h="100vh">
                <Loader />
            </Stack>
        );
    }

    return (
        <Stack align="center" justify="center" h="100vh" p="xl">
            <Card withBorder radius="xl" p="xl" w={440}>
                <Stack gap="lg">
                    <Stack gap={4}>
                        <Title order={4}>Select your Amazon marketplaces</Title>
                        <Text size="sm" c="dimmed">
                            Choose the marketplaces you want Reparo to sync data
                            for.
                        </Text>
                    </Stack>

                    {availableIds.length === 0 ? (
                        <Text size="sm" c="dimmed">
                            No marketplaces were returned by Amazon. Please
                            contact support.
                        </Text>
                    ) : (
                        <Stack gap="xs">
                            {availableIds.map((id) => (
                                <Checkbox
                                    key={id}
                                    label={MARKETPLACE_NAMES[id] ?? id}
                                    checked={selected.includes(id)}
                                    onChange={() => toggle(id)}
                                />
                            ))}
                        </Stack>
                    )}

                    {err && (
                        <Text size="sm" c="red">
                            {err}
                        </Text>
                    )}

                    <Button
                        radius="xl"
                        onClick={confirm}
                        loading={saving}
                        disabled={availableIds.length === 0}
                    >
                        Confirm & continue
                    </Button>
                </Stack>
            </Card>
        </Stack>
    );
};

export default AmazonMarketplaceSelect;
